import { defineStore } from 'pinia'
import axios from '@/utils/axios'
import { identifyUser, resetUser } from '@/utils/tracking'
import mixpanel from '@/plugins/mixpanel'
import { SIGNED_UP, LOGGED_IN, LOGGED_OUT } from '@/constants/events'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
    isGoogleUser: (state) => state.user?.authProvider === 'google',
    userAvatar: (state) => state.user?.avatar
  },

  actions: {
    async registerSendOtp(userData) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post('/qrcloneapi/api/auth/register/send-otp', userData)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to send verification code'
        throw error
      } finally {
        this.loading = false
      }
    },

    async registerVerify({ email, code }) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post('/qrcloneapi/api/auth/register/verify', {
          email,
          code
        })
        this.token = response.data.token
        this.user = response.data.user
        localStorage.setItem('token', this.token)
        identifyUser(this.user)
        mixpanel.track(SIGNED_UP, {
          email: this.user.email,
          auth_provider: this.user.authProvider,
          plan: this.user.plan,
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.error || 'Verification failed'
        throw error
      } finally {
        this.loading = false
      }
    },

    async login(credentials) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post('/qrcloneapi/api/auth/login', credentials)
        this.token = response.data.token
        this.user = response.data.user
        localStorage.setItem('token', this.token)
        identifyUser(this.user)
        mixpanel.track(LOGGED_IN, {
          email: this.user.email,
          auth_provider: this.user.authProvider,
          plan: this.user.plan,
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.error || 'Login failed'
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchProfile() {
      try {
        const response = await axios.get('/qrcloneapi/api/auth/profile')
        this.user = response.data.user
        // Re-identify on page refresh so returning users are linked correctly
        identifyUser(this.user)
        return response.data.user
      } catch (error) {
        this.logout()
        throw error
      }
    },

    async updateProfile(updates) {
      try {
        const response = await axios.put('/qrcloneapi/api/auth/profile', updates)
        this.user = response.data.user
        return response.data.user
      } catch (error) {
        throw error
      }
    },

    async getDeleteAccountWarnings() {
      const response = await axios.get('/qrcloneapi/api/auth/delete-account-warnings')
      return response.data
    },

    async deleteAccount() {
      await axios.delete('/qrcloneapi/api/auth/delete-account')
      this.logout()
    },

    logout() {
      // Fire before resetUser() so the event still carries the user's identity
      mixpanel.track(LOGGED_OUT, {
        email: this.user?.email,
        plan: this.user?.plan,
      })
      resetUser()
      this.token = null
      this.user = null
      localStorage.removeItem('token')
    },

    initializeAuth() {
      if (this.token) {
        this.fetchProfile().catch(() => {
          this.logout()
        })
      }
    }
  }
})
